import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import PageTitle from "../../../../components/ui/PageTitle";
import ChannelForm from "../../../../components/admin/ChannelForm";
import Button from "../../../../components/ui/Button";
import LoadingIndicator from "../../../../components/ui/LoadingIndicator";
import { createAdminChannel, getMyClientConfig } from "../../../../lib/apiClient";
import { ChannelCreatePayload } from "../../../../types";
import { getErrorMessage } from "../../../../lib/utils";
import toast from "react-hot-toast";
import { ROUTES } from "../../../../lib/constants";

export default function AdminChannelNewPage() {
  const router = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingConfig, setIsLoadingConfig] = useState(true);
  const [formError, setFormError] = useState<string | null>(null);
  const [integrationFlags, setIntegrationFlags] = useState({
    telegramEnabled: true,
    discordEnabled: true,
    slackEnabled: true,
  });
  const [telegramConfigured, setTelegramConfigured] = useState(true);

  useEffect(() => {
    const loadIntegrationFlags = async () => {
      setIsLoadingConfig(true);
      try {
        const config = await getMyClientConfig();
        const features = config?.config?.platform?.features || {};
        const tg = config?.config?.platform?.telegram || {};
        const hasBotToken = !!(tg as any).botToken;
        const hasBotUsername = !!(tg as any).botUsername;
        setTelegramConfigured(hasBotToken && hasBotUsername);
        setIntegrationFlags({
          telegramEnabled: features.telegramEnabled !== false,
          discordEnabled: features.discordEnabled !== false,
          slackEnabled: features.slackEnabled !== false,
        });
      } catch (err) {
        setTelegramConfigured(false);
        setIntegrationFlags({
          telegramEnabled: true,
          discordEnabled: true,
          slackEnabled: true,
        });
      } finally {
        setIsLoadingConfig(false);
      }
    };

    loadIntegrationFlags();
  }, []);

  const handleSubmit = async (formData: ChannelCreatePayload) => {
    setIsSubmitting(true);
    setFormError(null);

    try {
      const newChannel = await createAdminChannel(formData);
      toast.success(`Channel '${newChannel.name}' created successfully.`, {
        id: "create-channel-success",
      });
      router(ROUTES.ADMIN_CHANNELS);
    } catch (err) {
      const message = getErrorMessage(err);
      setFormError(message);
      toast.error(`Failed to create channel: ${message}`, {
        id: "create-channel-error",
      });
      setIsSubmitting(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4 px-8">
        <PageTitle
          title="Add Channel"
          subtitle="Add a Telegram, Discord, or Slack channel — Telegram channels are detected automatically"
        />
        <Button
          variant="secondary"
          onClick={() => router(ROUTES.ADMIN_CHANNELS)}
          disabled={isSubmitting}
        >
          Back to Channels
        </Button>
      </div>

      {isLoadingConfig ? (
        <LoadingIndicator text="Loading integration permissions..." />
      ) : !telegramConfigured ? (
        <div className="mx-8 mt-4 rounded-lg border border-functional-warning/40 bg-functional-warning/10 p-6 flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="flex-1">
            <p className="text-base font-semibold text-functional-warning mb-1">Telegram bot not configured</p>
            <p className="text-sm text-text-secondary">
              Bot Token and Bot Username must be saved in{" "}
              <strong className="text-text-primary">Settings → Integrations</strong>{" "}
              before you can add a Telegram channel.
            </p>
          </div>
          <Button onClick={() => router(ROUTES.ADMIN_SETTINGS)}>
            Go to Settings
          </Button>
        </div>
      ) : (
        <ChannelForm
          onSubmit={handleSubmit}
          isLoading={isSubmitting}
          formError={formError}
          isEditMode={false}
          integrationFlags={integrationFlags}
        />
      )}
    </div>
  );
}
